import { Layout } from "@/components/layout/Layout";
import CandlestickLoader from "@/components/common/CandlestickLoader";
import { SSEDebugPanel } from "@/components/SSEDebugPanel";
import { useAuth } from "@/contexts/AuthContext";
import { StudiesRealtimeProvider } from "@/contexts/StudiesRealtimeProvider";
import { useLocation } from "react-router-dom";
import { useGlobalStudiesCache } from "@/hooks/useGlobalStudiesCache";

const PROTECTED_LAYOUT_MESSAGES = [
  "Securing the exchange tunnel",
  "Clocking in the trading floor",
  "Syncing multi-tenant safeguards",
  "Bootstrapping mission control",
];

const REDIRECT_MESSAGES = [
  "Redirecting to AI Edge...",
];

export function ProtectedLayout() {
  const { isLoading, authStatus, login } = useAuth();
  const location = useLocation();
  
  // Keep global studies cache warm while authenticated.
  // This hook MUST be called unconditionally (before any early returns)
  // to satisfy React's Rules of Hooks. The hook internally uses
  // `enabled: isAuthenticated` so it's a no-op when not authenticated.
  useGlobalStudiesCache();

  if (isLoading) {
    return (
      <div className="flex min-h-screen w-screen items-center justify-center bg-background px-6">
        <CandlestickLoader
          isLoading
          context="ProtectedLayout"
          containerClassName="w-full min-h-[70vh]"
          messageClassName="text-slate-200"
          messages={PROTECTED_LAYOUT_MESSAGES}
        />
      </div>
    );
  }

  // User is logged into AIEW but lacks optimizer entitlement
  // AuthContext is handling the redirect to AI Edge's no-access page
  if (authStatus === 'no-access') {
    return (
      <div className="flex min-h-screen w-screen items-center justify-center bg-background px-6">
        <CandlestickLoader
          isLoading
          context="ProtectedLayout"
          containerClassName="w-full min-h-[70vh]"
          messageClassName="text-slate-200"
          messages={REDIRECT_MESSAGES}
        />
      </div>
    );
  }

  if (authStatus === 'unauthenticated') {
    console.warn("🚪 [ProtectedLayout] User not authenticated, redirecting to AIEW login", {
      timestamp: new Date().toISOString(),
      location: location.pathname,
    });
    // Redirect directly to AIEW login instead of defunct /login page
    login();
    return null;
  }

  // Wrap Layout with StudiesRealtimeProvider to ensure SSE connection
  // is established once for all authenticated routes. This provides
  // automatic cache updates for all ["studies", ...] queries.
  return (
    <StudiesRealtimeProvider>
      <Layout />
      <SSEDebugPanel />
    </StudiesRealtimeProvider>
  );
}
